// "Go to EMR" (native app): which EMRs get the button, and which URL it opens.
//
// EMR_LOGIN_URLS also feeds EMR Directives / Instruction Push, which match
// configs by host, so the landing pages that differ from it live here instead
// of changing it.
import { EMR_LOGIN_URLS } from './EmrNoteTypeMapping';

// Where Go to EMR lands when EMR_LOGIN_URLS is wrong for a fresh sign-in.
const LANDING_OVERRIDES = {
  // Security/login clears Clinicient's sessionStorage on load. Enter the app
  // so an existing session survives and signed-out users follow its auth flow.
  Clinicient: 'https://insight.clinicient.com/GO/',
  // /login shows Prompt's 404 after sign-in; let the root choose the
  // appropriate sign-in or authenticated landing page.
  Prompt: 'https://go.promptemr.com/',
  // /login is not an Empower page: signed out it bounces to the sign-in form
  // with ReturnUrl=/login, so Empower sends the user to /login after signing
  // in, and that page is a 404 (as is /login when already signed in).
  Empower: 'https://app.empoweremr.com/',
  // stridethera.com is Stride's marketing site (redirects to strideemr.ai);
  // the app is on the app. subdomain.
  StrideThera: 'https://app.stridethera.com/',
};

// The EMR field on a user is free-form ("webpt2", "Stride", "CORA", ...).
// Spellings are compared lowercase with only letters and digits kept.
const ALIASES = {
  stride: 'StrideThera',
  cora: 'Cora Health',
  insightgo: 'Clinicient',
  webpt1: 'WebPT 1.0',
  webpt10: 'WebPT 1.0',
  // WebPT SOAP 2.0 (emr.webpt.com) is reached by signing in on app.webpt.com;
  // emr.webpt.com itself answers 404.
  webpt2: 'WebPT 2.0',
  webpt20: 'WebPT 2.0',
};

// On by default (no go_to_emr flag needed). Anyone else needs the flag.
export const GO_TO_EMR_DEFAULT_EMRS = [
  'Clinicient',
  'Empower',
  'Prompt',
  'StrideThera',
  'WebPT',
  'WebPT 1.0',
  'WebPT 2.0',
];

const normalize = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const CANONICAL_BY_NORMALIZED = Object.keys(EMR_LOGIN_URLS).reduce(
  (acc, name) => ({ ...acc, [normalize(name)]: name }),
  {},
);

/** The EMR_LOGIN_URLS name for a user's EMR spelling, or null. */
export function canonicalGoToEmr(emr) {
  const key = normalize(emr);
  if (!key) return null;
  return ALIASES[key] || CANONICAL_BY_NORMALIZED[key] || null;
}

/** The page Go to EMR opens for this EMR, or '' when we don't know it. */
export function goToEmrUrl(emr) {
  const name = canonicalGoToEmr(emr);
  if (!name) return '';
  return LANDING_OVERRIDES[name] || EMR_LOGIN_URLS[name] || '';
}

/** Whether the Go to EMR button shows for this EMR without the flag. */
export function goToEmrOnByDefault(emr) {
  return GO_TO_EMR_DEFAULT_EMRS.includes(canonicalGoToEmr(emr));
}
